export interface CornerRect {
  left: number
  top: number
  width: number
  height: number
  textAlign: 'left' | 'right'
  verticalAlign: 'top' | 'bottom'
}

interface Rect {
  row: number
  col: number
  width: number
  height: number
  area: number
}

// Classic "largest rectangle in a binary matrix": accumulate per-column
// run-heights row by row, and at each row solve "largest rectangle in a
// histogram" with a monotonic stack. O(rows * cols) overall.
//
// Text reads better in landscape/square boxes than portrait ones, so among
// candidates we only ever consider rectangles that are at least as wide as
// they are tall — a plain largest-area search can otherwise return a tall,
// narrow sliver just because it edges out a slightly smaller landscape box.
// A 1x1 cell is always square, so a landscape/square candidate exists
// whenever the grid has any filled cell at all.
function largestRectangle(grid: boolean[], rows: number, cols: number): Rect | undefined {
  const heights = new Array<number>(cols).fill(0)
  let best: Rect | undefined

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      heights[col] = grid[row * cols + col] ? heights[col]! + 1 : 0
    }

    const stack: number[] = []
    for (let col = 0; col <= cols; col++) {
      const h = col < cols ? heights[col]! : 0
      while (stack.length && heights[stack[stack.length - 1]!]! >= h) {
        const height = heights[stack.pop()!]!
        const left = stack.length ? stack[stack.length - 1]! + 1 : 0
        const width = col - left
        const area = height * width
        if (width >= height && (!best || area > best.area)) {
          best = { row: row - height + 1, col: left, width, height, area }
        }
      }
      stack.push(col)
    }
  }

  return best
}

function flipVertically(grid: boolean[], rows: number, cols: number): boolean[] {
  const flipped = new Array<boolean>(rows * cols)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      flipped[row * cols + col] = grid[(rows - 1 - row) * cols + col]!
    }
  }
  return flipped
}

// A rectangle's left edge only marks a genuine silhouette boundary if the
// shape stops right there. If the shape is still mostly solid one column
// further left, that edge is just an artifact of the area-maximizing search
// (a taller/shorter box happened to win elsewhere), not a real edge — in
// that case the leftover slack from group text-size syncing should collect
// on the left (right-aligned text) so the text hugs the side that IS real.
// A simple majority vote (rather than requiring every single row) keeps this
// robust against a small notch clipping only part of the rect's height.
function isMostlyFilledColumn(grid: boolean[], cols: number, col: number, rowStart: number, rowEnd: number): boolean {
  if (col < 0 || col >= cols) return false
  let filled = 0
  const total = rowEnd - rowStart
  for (let row = rowStart; row < rowEnd; row++) {
    if (grid[row * cols + col]) filled += 1
  }
  return filled / total > 0.5
}

// Same reasoning as isMostlyFilledColumn, rotated 90 degrees: a rect's top
// edge only marks a genuine silhouette boundary if the shape stops right
// there. If the row just above is still mostly filled, that edge is an
// artifact of the area-maximizing search, not real — so leftover slack from
// group text-size syncing should collect at the top (bottom-aligned text) to
// hug the side that IS real.
function isMostlyFilledRow(grid: boolean[], cols: number, row: number, colStart: number, colEnd: number): boolean {
  if (row < 0) return false
  let filled = 0
  const total = colEnd - colStart
  for (let col = colStart; col < colEnd; col++) {
    if (grid[row * cols + col]) filled += 1
  }
  return filled / total > 0.5
}

const EDGE_SAMPLES = 16
const REFINE_ITERATIONS = 12

// Cell-center sampling only pins the rect to the coarse grid line, leaving up
// to one full grid cell of slack between it and the shape's true silhouette
// edge. Bisecting each edge independently against a continuous isPointInFill
// scan (bounded to one cell beyond the grid line) snaps it to the real
// sub-pixel boundary without reconsidering which rectangle won.
function isLineFilled(shape: SVGGeometryElement, x1: number, y1: number, x2: number, y2: number): boolean {
  for (let i = 0; i <= EDGE_SAMPLES; i++) {
    const t = i / EDGE_SAMPLES
    if (!shape.isPointInFill(new DOMPoint(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t))) return false
  }
  return true
}

// `knownFilled` must satisfy `isFilled` — the search only ever moves toward
// `limit`, so a bad `knownFilled` just degrades back to the unrefined edge
// instead of producing a wrong (unfilled) result.
function refineEdge(isFilled: (pos: number) => boolean, knownFilled: number, limit: number): number {
  let lo = knownFilled
  let hi = limit
  for (let i = 0; i < REFINE_ITERATIONS; i++) {
    const mid = (lo + hi) / 2
    if (isFilled(mid)) lo = mid
    else hi = mid
  }
  return lo
}

// Organic blob shapes rarely fill their own bounding box corners, so a text
// box naively placed at (0,0) can land in a transparent, clipped-away area.
// Instead we rasterize the shape onto a fixed-resolution grid (via
// isPointInFill, which every SVG geometry element supports natively) and
// find the single largest all-filled rectangle in it, then bisect its edges
// for sub-pixel precision. This is the fallback for shapes findExactCornerRect
// can't handle (anything with a curve) — see that function for the exact path.
function findSampledCornerRect(
  shape: SVGGeometryElement,
  viewBox: { x?: number, y?: number, width: number, height: number },
  corner: 'top-left' | 'bottom-left',
  resolution: number,
): CornerRect | undefined {
  const originX = viewBox.x ?? 0
  const originY = viewBox.y ?? 0
  const cellWidth = viewBox.width / resolution
  const cellHeight = viewBox.height / resolution

  const grid = new Array<boolean>(resolution * resolution)
  for (let row = 0; row < resolution; row++) {
    for (let col = 0; col < resolution; col++) {
      const x = originX + (col + 0.5) * cellWidth
      const y = originY + (row + 0.5) * cellHeight
      grid[row * resolution + col] = shape.isPointInFill(new DOMPoint(x, y))
    }
  }

  const searchGrid = corner === 'top-left' ? grid : flipVertically(grid, resolution, resolution)
  const found = largestRectangle(searchGrid, resolution, resolution)
  if (!found) return undefined

  const row = corner === 'top-left' ? found.row : resolution - found.row - found.height
  const textAlign = isMostlyFilledColumn(grid, resolution, found.col - 1, row, row + found.height)
    ? 'right'
    : 'left'
  const verticalAlign = isMostlyFilledRow(grid, resolution, row - 1, found.col, found.col + found.width)
    ? 'bottom'
    : 'top'

  const left0 = originX + found.col * cellWidth
  const right0 = originX + (found.col + found.width) * cellWidth
  const top0 = originY + row * cellHeight
  const bottom0 = originY + (row + found.height) * cellHeight

  const left = refineEdge(x => isLineFilled(shape, x, top0, x, bottom0), left0, Math.max(originX, left0 - cellWidth))
  const right = refineEdge(x => isLineFilled(shape, x, top0, x, bottom0), right0, Math.min(originX + viewBox.width, right0 + cellWidth))
  const top = refineEdge(y => isLineFilled(shape, left0, y, right0, y), top0, Math.max(originY, top0 - cellHeight))
  const bottom = refineEdge(y => isLineFilled(shape, left0, y, right0, y), bottom0, Math.min(originY + viewBox.height, bottom0 + cellHeight))

  return {
    left: ((left - originX) / viewBox.width) * 100,
    top: ((top - originY) / viewBox.height) * 100,
    width: ((right - left) / viewBox.width) * 100,
    height: ((bottom - top) / viewBox.height) * 100,
    textAlign,
    verticalAlign,
  }
}

// For a rectilinear shape (every edge axis-aligned), the optimal rectangle's
// boundary is provably always one of the shape's own vertex coordinates —
// there's never a reason for it to stop short or overshoot mid-edge. So
// instead of sampling a fixed grid and hoping it lands close enough, we
// tile the viewBox using the shape's *actual* x/y coordinates as grid
// lines. Every resulting cell is then guaranteed to be purely inside or
// outside the shape (it can't straddle an edge, since edges only occur on
// grid lines by construction), so a single isPointInFill sample per cell is
// exact — not an approximation — and the search runs in a few hundred
// cells instead of resolution^2.
//
// That only holds when every segment is a line, so a path with a curve
// anywhere sends the caller to the sampled fallback.
function parseRectilinearPath(d: string): { x: number, y: number }[] | undefined {
  const parsed = parseSvgPathPoints(d)
  return parsed && !parsed.curved ? parsed.points : undefined
}

function getRectilinearVertices(shape: SVGGeometryElement): { x: number, y: number }[] | undefined {
  const tag = shape.tagName.toLowerCase()

  if (tag === 'rect') {
    const x = parseFloat(shape.getAttribute('x') ?? '0')
    const y = parseFloat(shape.getAttribute('y') ?? '0')
    const width = parseFloat(shape.getAttribute('width') ?? '0')
    const height = parseFloat(shape.getAttribute('height') ?? '0')
    if (parseFloat(shape.getAttribute('rx') ?? '0') || parseFloat(shape.getAttribute('ry') ?? '0')) return undefined
    return [{ x, y }, { x: x + width, y }, { x: x + width, y: y + height }, { x, y: y + height }]
  }

  if (tag === 'polygon' || tag === 'polyline') {
    const pointsAttr = shape.getAttribute('points')
    if (!pointsAttr) return undefined
    const numbers = pointsAttr.trim().split(/[\s,]+/).map(Number)
    if (numbers.length < 4 || numbers.some(Number.isNaN)) return undefined
    const points: { x: number, y: number }[] = []
    for (let i = 0; i + 1 < numbers.length; i += 2) points.push({ x: numbers[i]!, y: numbers[i + 1]! })
    return points
  }

  if (tag === 'path') {
    const d = shape.getAttribute('d')
    return d ? parseRectilinearPath(d) : undefined
  }

  return undefined
}

interface WeightedRect {
  row: number
  col: number
  rowSpan: number
  colSpan: number
  area: number
}

// Same monotonic-stack algorithm as largestRectangle, generalized to
// non-uniform cell sizes: `heights` accumulates real column height (not a
// cell count), and width is read from a column-width prefix sum. `runStart`
// tracks which row each column's current filled run began at, since a real
// height sum can no longer be turned back into a row count by subtraction.
function largestRectangleWeighted(
  grid: boolean[],
  rows: number,
  cols: number,
  colWidths: number[],
  rowHeights: number[],
): WeightedRect | undefined {
  const colPrefix = new Array<number>(cols + 1).fill(0)
  for (let i = 0; i < cols; i++) colPrefix[i + 1] = colPrefix[i]! + colWidths[i]!

  const heights = new Array<number>(cols).fill(0)
  const runStart = new Array<number>(cols).fill(0)
  let best: WeightedRect | undefined

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (grid[row * cols + col]) {
        if (heights[col] === 0) runStart[col] = row
        heights[col] = heights[col]! + rowHeights[row]!
      } else {
        heights[col] = 0
      }
    }

    const stack: number[] = []
    for (let col = 0; col <= cols; col++) {
      const h = col < cols ? heights[col]! : 0
      while (stack.length && heights[stack[stack.length - 1]!]! >= h) {
        const poppedCol = stack.pop()!
        const height = heights[poppedCol]!
        const left = stack.length ? stack[stack.length - 1]! + 1 : 0
        const width = colPrefix[col]! - colPrefix[left]!
        const area = height * width
        if (width >= height && (!best || area > best.area)) {
          best = { row: runStart[poppedCol]!, col: left, rowSpan: row - runStart[poppedCol]! + 1, colSpan: col - left, area }
        }
      }
      stack.push(col)
    }
  }

  return best
}

function isMostlyFilledColumnWeighted(grid: boolean[], cols: number, col: number, rowStart: number, rowEnd: number, rowHeights: number[]): boolean {
  if (col < 0 || col >= cols) return false
  let filled = 0
  let total = 0
  for (let row = rowStart; row < rowEnd; row++) {
    total += rowHeights[row]!
    if (grid[row * cols + col]) filled += rowHeights[row]!
  }
  return total > 0 && filled / total > 0.5
}

function isMostlyFilledRowWeighted(grid: boolean[], cols: number, row: number, colStart: number, colEnd: number, colWidths: number[]): boolean {
  if (row < 0) return false
  let filled = 0
  let total = 0
  for (let col = colStart; col < colEnd; col++) {
    total += colWidths[col]!
    if (grid[row * cols + col]) filled += colWidths[col]!
  }
  return total > 0 && filled / total > 0.5
}

function findExactCornerRect(
  shape: SVGGeometryElement,
  viewBox: { x?: number, y?: number, width: number, height: number },
  corner: 'top-left' | 'bottom-left',
): CornerRect | undefined {
  const vertices = getRectilinearVertices(shape)
  if (!vertices || vertices.length < 3) return undefined

  const minX = viewBox.x ?? 0
  const minY = viewBox.y ?? 0
  const maxX = minX + viewBox.width
  const maxY = minY + viewBox.height

  const xs = [...new Set(vertices.map(v => v.x).filter(x => x > minX && x < maxX))].sort((a, b) => a - b)
  const ys = [...new Set(vertices.map(v => v.y).filter(y => y > minY && y < maxY))].sort((a, b) => a - b)
  xs.unshift(minX)
  xs.push(maxX)
  ys.unshift(minY)
  ys.push(maxY)

  const cols = xs.length - 1
  const rows = ys.length - 1
  if (cols < 1 || rows < 1) return undefined

  const colWidths = Array.from({ length: cols }, (_, i) => xs[i + 1]! - xs[i]!)
  const rowHeights = Array.from({ length: rows }, (_, j) => ys[j + 1]! - ys[j]!)

  const grid = new Array<boolean>(rows * cols)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = (xs[col]! + xs[col + 1]!) / 2
      const y = (ys[row]! + ys[row + 1]!) / 2
      grid[row * cols + col] = shape.isPointInFill(new DOMPoint(x, y))
    }
  }

  const searchGrid = corner === 'top-left' ? grid : flipVertically(grid, rows, cols)
  const searchRowHeights = corner === 'top-left' ? rowHeights : [...rowHeights].reverse()
  const found = largestRectangleWeighted(searchGrid, rows, cols, colWidths, searchRowHeights)
  if (!found) return undefined

  const row = corner === 'top-left' ? found.row : rows - found.row - found.rowSpan
  const textAlign = isMostlyFilledColumnWeighted(grid, cols, found.col - 1, row, row + found.rowSpan, rowHeights)
    ? 'right'
    : 'left'
  const verticalAlign = isMostlyFilledRowWeighted(grid, cols, row - 1, found.col, found.col + found.colSpan, colWidths)
    ? 'bottom'
    : 'top'

  const left = xs[found.col]!
  const right = xs[found.col + found.colSpan]!
  const top = ys[row]!
  const bottom = ys[row + found.rowSpan]!

  return {
    left: ((left - minX) / viewBox.width) * 100,
    top: ((top - minY) / viewBox.height) * 100,
    width: ((right - left) / viewBox.width) * 100,
    height: ((bottom - top) / viewBox.height) * 100,
    textAlign,
    verticalAlign,
  }
}

export function findCornerRect(
  shape: SVGGeometryElement,
  viewBox: { x?: number, y?: number, width: number, height: number },
  corner: 'top-left' | 'bottom-left',
  resolution = 128,
): CornerRect | undefined {
  return findExactCornerRect(shape, viewBox, corner) ?? findSampledCornerRect(shape, viewBox, corner, resolution)
}
